import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/release-notes")({
  head: () => ({
    meta: [
      { title: "Release Notes - Smartasaurus" },
      {
        name: "description",
        content:
          "What's new, fixed, and improved on Smartasaurus. A running changelog of the curiosity platform.",
      },
      { property: "og:title", content: "Release Notes - Smartasaurus" },
      { property: "og:description", content: "What's new, fixed, and improved on Smartasaurus." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://smartasaurus.com/release-notes" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/release-notes" }],
  }),
  component: ReleaseNotesPage,
});

type Change = { type: "new" | "fixed" | "improved"; text: string };
type Release = { version: string; date: string; title: string; changes: Change[] };

const RELEASES: Release[] = [
  {
    version: "v2.0",
    date: "2026-09-30",
    title: "A more personal way to explore",
    changes: [
      {
        type: "improved",
        text: "A redesigned mobile header keeps the full Smartasaurus name, menu, search, language picker, profile and progress easy to reach.",
      },
      {
        type: "new",
        text: "Your personal rank now grows across article certifications and quizzes, with progress toward your next title always visible.",
      },
      {
        type: "improved",
        text: "Language switching is clearer across navigation, articles and quizzes, with reviewed article editions served when available.",
      },
      {
        type: "improved",
        text: "Article playback is easier to use on desktop, while written stories remain comfortable to read on every screen.",
      },
      {
        type: "improved",
        text: "Quiz answers are randomized, progress is clearer and the next challenge is always one tap away.",
      },
    ],
  },
];


function badgeClass(type: Change["type"]) {
  switch (type) {
    case "new":
      return "bg-primary/15 text-primary border-primary/30";
    case "fixed":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    case "improved":
      return "bg-orange-500/15 text-orange-400 border-orange-500/30";
  }
}

function badgeLabel(type: Change["type"]) {
  return { new: "New", fixed: "Fixed", improved: "Improved" }[type];
}

function ReleaseNotesPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-16 md:px-8 md:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          · Product updates
        </div>
        <h1 className="mt-3 font-display text-5xl uppercase leading-[0.95] md:text-6xl">
          Release <span className="text-gradient-curiosity">notes</span>.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          New features and improvements for everyone using Smartasaurus.
        </p>


        <div className="mt-12 space-y-10">
          {RELEASES.map((r) => (
            <section
              key={r.version}
              className="rounded-lg border border-border bg-card/40 p-6 md:p-8"
            >
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/50 pb-3">
                <div>
                  <div className="font-display text-2xl">
                    {r.version} · {r.title}
                  </div>
                </div>
                <time className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {r.date}
                </time>
              </header>
              <ul className="mt-4 space-y-3">
                {r.changes.map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${badgeClass(c.type)}`}
                    >
                      {badgeLabel(c.type)}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground/90">{c.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
